import { useState, useEffect } from "react"
import { Folder, FolderUp, ChevronRight, Plus, Check, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Folder {
  name: string
  path: string
}

interface FolderPickerProps {
  open: boolean
  onClose: () => void
  onSelect: (path: string) => void
  currentPath?: string
  rootPath?: string
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

interface FolderPickerInlineProps {
  onSelect: (path: string) => void
  currentPath?: string
  rootPath?: string
}

export function FolderPickerInline({ onSelect, currentPath, rootPath }: FolderPickerInlineProps) {
  const [folders, setFolders] = useState<Folder[]>([])
  const [selectedPath, setSelectedPath] = useState(currentPath || "")
  const [breadcrumbs, setBreadcrumbs] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState("")

  const effectiveRoot = rootPath || ""
  const rootParts = effectiveRoot.split("/").filter(Boolean)
  const hasRoot = rootParts.length > 0
  const rootName = hasRoot ? rootParts[rootParts.length - 1] : "Root"

  useEffect(() => {
    loadFolders(effectiveRoot)
  }, [])

  function buildBreadcrumbs(path: string) {
    if (!path) return []
    const allParts = path.split("/").filter(Boolean)
    return allParts.slice(rootParts.length)
  }

  async function loadFolders(path: string) {
    setLoading(true)
    try {
      const res = await fetch(`/api/folders?path=${encodeURIComponent(path)}`, {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      const data = await res.json()
      setFolders(data.folders || [])
      setSelectedPath(path)
      setBreadcrumbs(buildBreadcrumbs(path))
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }

  async function handleCreateFolder() {
    if (!newFolderName.trim()) return

    const path = `${selectedPath}/${newFolderName.trim()}`
    try {
      await fetch("/api/folders", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({ path }),
      })
      setCreatingFolder(false)
      setNewFolderName("")
      loadFolders(selectedPath)
    } catch {
      // ignore
    }
  }

  function handleBreadcrumbClick(index: number) {
    const rootParts = effectiveRoot.split("/").filter(Boolean)
    const targetParts = [...rootParts, ...breadcrumbs.slice(0, index + 1)]
    loadFolders("/" + targetParts.join("/"))
  }

  return (
    <div>
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 overflow-x-auto border-b px-4 py-2 text-sm">
        {hasRoot && (
          <>
            <button
              onClick={() => loadFolders("")}
              className="shrink-0 text-muted-foreground hover:text-foreground"
              title="Navigate above root folder"
            >
              <FolderUp className="h-4 w-4" />
            </button>
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          </>
        )}
        <button
          onClick={() => loadFolders(effectiveRoot)}
          className="shrink-0 text-muted-foreground hover:text-foreground"
        >
          {rootName}
        </button>
        {breadcrumbs.map((part, i) => (
          <span key={i} className="flex items-center gap-1">
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <button
              onClick={() => handleBreadcrumbClick(i)}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              {part}
            </button>
          </span>
        ))}
      </div>

      {/* Folder list */}
      <div className="max-h-64 overflow-y-auto p-2">
        {loading ? (
          <p className="p-4 text-center text-sm text-muted-foreground">Loading...</p>
        ) : folders.length === 0 ? (
          <p className="p-4 text-center text-sm text-muted-foreground">No subfolders</p>
        ) : (
          folders.map((folder) => (
            <button
              key={folder.path}
              onClick={() => loadFolders(folder.path)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-accent"
            >
              <Folder className="h-4 w-4 text-muted-foreground" />
              {folder.name}
            </button>
          ))
        )}
      </div>

      {/* New folder */}
      <div className="border-t p-3">
        {creatingFolder ? (
          <div className="flex items-center gap-2">
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            />
            <Button size="sm" onClick={handleCreateFolder}>
              <Check className="h-4 w-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCreatingFolder(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setCreatingFolder(true)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <Plus className="h-4 w-4" />
            New folder
          </button>
        )}
      </div>

      {/* Select button */}
      <div className="border-t p-4">
        <div className="mb-2 text-xs text-muted-foreground">
          Selected: {selectedPath || "/"}
        </div>
        <Button onClick={() => onSelect(selectedPath)} className="w-full">
          Select this folder
        </Button>
      </div>
    </div>
  )
}

export function FolderPicker({ open, onClose, onSelect, currentPath, rootPath }: FolderPickerProps) {
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50" data-testid="folder-picker" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-xl md:rounded-xl bg-background shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b p-4">
          <h3 className="font-semibold">Choose folder</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>
        <FolderPickerInline
          onSelect={onSelect}
          currentPath={currentPath}
          rootPath={rootPath}
        />
      </div>
    </div>
  )
}
